import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { syncPushSubscription } from '../lib/push';
import type { UserRole } from '../lib/authApi';

interface ProtectedRouteProps {
  children: ReactNode;
  // Optional role gate. When set, the user must hold one of these roles.
  roles?: UserRole[];
}

export function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const location = useLocation();
  const initializing = useAuthStore((s) => s.initializing);
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);

  // Re-register this browser's push subscription once a session exists. The
  // browser can rotate or drop a subscription on its own, and the service
  // worker holds no access token to report that with — so the app tells the
  // server what it currently has, every time someone signs in. No-ops unless
  // notifications are already switched on here.
  useEffect(() => {
    if (accessToken) void syncPushSubscription();
  }, [accessToken]);

  // Wait for the initial session check before deciding to redirect.
  if (initializing) {
    return <div className="p-8 text-gray-500">Se încarcă...</div>;
  }

  if (!accessToken || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
